import { forwardRef, useEffect, useRef } from "react";
import styles from "./FormValidationSummary.module.css";

export type ValidationSummaryEntry = {
  /** Stable key used for the React list and the optional `data-field` hook. */
  fieldKey: string;
  /** Human-readable label matching what the user sees beside the field. */
  fieldLabel: string;
  /** Human-readable error message (from Zod or an equivalent validator). */
  message: string;
};

type Props = {
  /** Sorted, deduped list of validation entries. */
  entries: ValidationSummaryEntry[];
  /**
   * Title rendered above the list. Defaults to the spec-mandated copy.
   */
  title?: string;
  /**
   * Optional click handler for each entry. Receives the underlying `fieldKey`
   * so consumers can focus the matching input. When provided, entries render
   * as buttons (anchor-styled) instead of plain text.
   */
  onEntryActivate?: (fieldKey: string) => void;
};

const DEFAULT_TITLE = "The form could not be submitted due to the following validation errors:";

/**
 * Top-of-page summary of form validation errors. Renders nothing when there
 * are no entries so callers can hang it above any form unconditionally.
 *
 * Behaviour:
 *  - Live region (`role="alert"`, `aria-live="assertive"`) so screen readers
 *    announce the summary the moment it appears.
 *  - The container is focused programmatically when entries first appear so
 *    sighted keyboard users see the block immediately and assistive tech
 *    re-reads it. `prefers-reduced-motion` is honoured for the scroll.
 *  - Numbered list of "field label — message" entries. When `onEntryActivate`
 *    is supplied, each entry becomes a link that focuses the offending input.
 */
const FormValidationSummary = forwardRef<HTMLDivElement, Props>(function FormValidationSummary(
  { entries, title = DEFAULT_TITLE, onEntryActivate },
  ref,
) {
  const internalRef = useRef<HTMLDivElement | null>(null);
  const setRef = (node: HTMLDivElement | null) => {
    internalRef.current = node;
    if (typeof ref === "function") {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  };

  // Scroll the summary into view + move focus when it first renders / changes.
  // We key the effect on the joined keys so re-validating the same fields still
  // re-announces (entries identity changes on every validate call anyway).
  const announceKey = entries.map(e => e.fieldKey).join("|");
  useEffect(() => {
    if (entries.length === 0) return;
    const node = internalRef.current;
    if (!node || typeof window === "undefined") return;
    const prefersReducedMotion =
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    window.scrollTo({ top: 0, behavior: prefersReducedMotion ? "auto" : "smooth" });
    queueMicrotask(() => {
      node.focus();
    });
  }, [announceKey, entries.length]);

  if (entries.length === 0) return null;

  return (
    <div
      ref={setRef}
      role="alert"
      aria-live="assertive"
      tabIndex={-1}
      data-testid="form-validation-summary"
      className={styles.summary}
    >
      <p className={styles.title}>{title}</p>
      <ol className={styles.list}>
        {entries.map(entry => (
          <li key={entry.fieldKey} data-field={entry.fieldKey}>
            {onEntryActivate ? (
              <a
                href={`#${entry.fieldKey}`}
                onClick={e => {
                  e.preventDefault();
                  onEntryActivate(entry.fieldKey);
                }}
              >
                {entry.fieldLabel}: {entry.message}
              </a>
            ) : (
              <>
                {entry.fieldLabel}: {entry.message}
              </>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
});

export default FormValidationSummary;
export { FormValidationSummary };
